import { Container, Grid, Button, Box, Alert, Typography } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useState, useEffect } from "react";
import SwapValidator from "../../DataEntity/SwapValidator";
import {
  TABLE_HEAD_SWAP_REBALANCE_ITEMS,
  TABLE_HEAD_SWAP_STATUS,
} from "../../../../../../component/Methods";
import { useSelector, useDispatch } from "react-redux";
import {
  addActiveRequests,
  updateLoading,
  updateLoadingMsg,
} from "../../../../../../Redux/Reducers/uiSlice";
import SwapRebalanceItems from "../../DataEntity/SwapRebalanceItems";
import SwapRebalanceValiditySkeleton from "../../../../../../component/spinner/Rebalancer/swap/SwapRebalanceValiditySkeleton";
import { sendWebSocketMessage } from "../../../../../../hooks/useWebSocket";
import { selectSwapRebalanceStrategiesValidity, selectSwapStrategiesAvailableDate, selectActiveRequests } from "../../../../../../Redux/Reducers/selectors";

const SwapStep3 = ({ setCurrentStep, contracts, executionStyles }) => { 
 const activeRequests = useSelector(selectActiveRequests);
  const swapRebalanceStrategiesValidity = useSelector(selectSwapRebalanceStrategiesValidity); 
  const swapStrategiesAvailableDate = useSelector(selectSwapStrategiesAvailableDate);
  const [selectedContract, setSelectedContract] = useState("all");
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [missingTickers, setMissingTickers] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const dispatch = useDispatch();

  useEffect(() => {
    if (swapRebalanceStrategiesValidity) {
      setFilteredContracts(swapRebalanceStrategiesValidity);
    }
  }, []);

  useEffect(() => {
    if (selectedContract === "all") {
      setFilteredContracts(swapRebalanceStrategiesValidity || []);
    } else {
      const filtered =
        swapRebalanceStrategiesValidity?.filter(
          (contract) => contract.id === selectedContract
        ) || [];
      setFilteredContracts(filtered);
    }
  }, [selectedContract, swapRebalanceStrategiesValidity]);

  useEffect(() => {
    const hasErrors = swapRebalanceStrategiesValidity?.every(
      (contract) => contract.validationErrors?.length > 0 && !contract.success
    );
    setHasValidationErrors(hasErrors);
    const allSuccessful = filteredContracts?.every(
      (contract) => contract.success === true
    );
    setShowSuccessMessage(!hasValidationErrors && allSuccessful);
  }, [swapRebalanceStrategiesValidity]);

  const handleNormalize = () => {
    const contractsWithErrors = swapRebalanceStrategiesValidity?.filter(
      (contract) => contract?.validationErrors?.length > 0
    );

    if (!contractsWithErrors?.length) return;

    const newMissingTickers = { ...missingTickers };

    contractsWithErrors.forEach((contract) => {
      if (!newMissingTickers[contract.id]) {
        newMissingTickers[contract.id] = [];
      }

      contract.validationErrors.forEach((error) => {
        if (!error?.ticker) return;

        const type = error.validationText[0]
          ?.toLowerCase()
          ?.startsWith("individual stock weight constraint is violated")
          ? "adjust"
          : "remove";

        const tickerData = {
          ticker: {
            currency: error?.ticker?.currency || 7,
            exchange: error?.ticker?.exchange || 4,
            investment: error?.ticker?.investment || 4,
            symbol: error?.ticker?.symbol,
          },
          weight: error?.weight,
          type: type,
        };

        const existingTickerIndex = newMissingTickers[contract.id].findIndex(
          (t) => t.ticker.symbol === tickerData.ticker.symbol
        );

        if (existingTickerIndex === -1) {
          newMissingTickers[contract.id].push(tickerData);
        }
      });
    });

    setMissingTickers(newMissingTickers);

    const socketRequest = {
      action: "rebalance_swaps",
      data: {
        strategyIds: contractsWithErrors.map((contract) => contract.id),
        step: 4,
        missingTickers: newMissingTickers,
        availableDates: swapStrategiesAvailableDate,
        executionStyles: executionStyles,
        newParams: {},
      },
    };
    sendWebSocketMessage(JSON.stringify(socketRequest));
    dispatch(addActiveRequests("rebalance_swaps"));
    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Normalizing Rebalancer Items"));
  };

  const onNext = () => {
    if (swapRebalanceStrategiesValidity?.length > 0) {
      const ids = swapRebalanceStrategiesValidity?.map(
        (contract) => contract.id
      );

      const newParams = {};
      swapRebalanceStrategiesValidity?.forEach((contract) => {
        if (contract.updatedParameters?.length > 0) {
          newParams[contract.id] = contract.updatedParameters.map((param) => ({
            stockId: param.stockId,
            newWeight: param.newWeight,
            executionStyle: param.executionStyle,
          }));
        }
      });

      const data = {
        strategyIds: ids,
        step: 5,
        newParams: newParams,
        availableDates: swapStrategiesAvailableDate,
        executionStyles: executionStyles,
      };

      const socketRequest = {
        action: "rebalance_swaps",
        data: data,
      };

      sendWebSocketMessage(JSON.stringify(socketRequest));
      dispatch(addActiveRequests("rebalance_swaps"));
      dispatch(updateLoading(true));
      dispatch(updateLoadingMsg("REBALANCE REQUEST SUBMITTED"));
    }

    setCurrentStep((prev) => prev + 1);
  };
  const isLoadingBlacklistedData = activeRequests.includes("rebalance_swaps");

  if (isLoadingBlacklistedData) {
    return <SwapRebalanceValiditySkeleton />;
  }

  return (
    <Container disableGutters maxWidth="100%" sx={{ marginTop: 2 }}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Box sx={{ width: "40%" }}>
              <Box
                sx={{
                  marginBottom: 1,
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography sx={{ fontWeight: "bold", fontSize: "20px" }}>
                  Validated Contracts
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <select
                    value={selectedContract}
                    onChange={(e) => setSelectedContract(e.target.value)}
                    style={{
                      padding: "5px",
                      fontSize: "14px",
                      marginBottom: 10,
                    }}
                    size="small"
                  >
                    <option value="all">All</option>
                    {swapRebalanceStrategiesValidity?.map((contract) => (
                      <option key={contract.id} value={contract.id}>
                        {contract.name}
                      </option>
                    ))}
                  </select>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                disabled={!hasValidationErrors}
                onClick={handleNormalize}
                sx={{
                  width: "200px",
                  height: "40px",
                  mx: 2,
                  backgroundColor: hasValidationErrors
                    ? "#FF9800"
                    : "rgba(0, 0, 0, 0.12)",
                  "&:hover": {
                    backgroundColor: hasValidationErrors
                      ? "#F57C00"
                      : "rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                Normalize
              </Button>
              <Button
                variant="contained"
                endIcon={<ArrowForwardIcon />}
                onClick={onNext}
                disabled={hasValidationErrors}
                sx={{ width: "200px", height: "40px" }}
              >
                Final Submission
              </Button>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              width: "100%",
              gap: 2,
            }}
          >
            {showSuccessMessage && (
              <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                Rebalance request submitted, you will be notified via email once
                its completed.
              </Alert>
            )}
            {!showSuccessMessage && filteredContracts?.length > 0 && (
              <>
                <Box sx={{ width: "30%" }}>
                  <SwapValidator
                    filteredContracts={filteredContracts}
                    tableHeader={TABLE_HEAD_SWAP_STATUS}
                    hasValidationErrors={hasValidationErrors}
                  />
                </Box>
                <Box sx={{ width: "30%" }}>
                  <SwapRebalanceItems
                    filteredContracts={filteredContracts}
                    tableHeader={TABLE_HEAD_SWAP_REBALANCE_ITEMS}
                    hasValidationErrors={hasValidationErrors}
                    filteredStrategies={true}
                  />
                </Box>
              </>
            )}
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SwapStep3;
